import clsx from 'clsx';
import Link from 'next/link';
import Icon, { type SvgIconInput } from '@publishing/components/ui/Icon';
import { ArrowArrow, EtcSquareMeter, EtcMeeting, TagCalendar } from '@publishing/components/ui/SvgSprite';
import { Picture } from '@publishing/components/ui/Picture';
import s from './CardRecommendBranch.module.scss';
import type { StaticImageData } from 'next/image';
import Badge, { BadgeGroup, type BadgeVariant } from '@publishing/components/ui/Badge';
import type { ReactNode } from 'react';

import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';

export type BranchCardTheme = 'primary' | 'secondary';

type BranchItemBase = {
  id?: string;
  tag?: BadgeVariant[];
  name: string;
  href: string;
  images: [string | StaticImageData, (string | StaticImageData)?];
  imageAlt?: string;
};

export type BranchDetailItem = BranchItemBase & {
  location?: string;
  details: {
    area: ReactNode;
    deposit: ReactNode;
    monthly: ReactNode;
  };
};

export type BranchCompactItem = BranchItemBase & {
  subTitle?: string;
};

type DetailField = {
  key: keyof BranchDetailItem['details'];
  term: ReactNode;
  iconName: SvgIconInput;
};

const DETAIL_FIELDS: DetailField[] = [
  { key: 'area', term: '전용 면적', iconName: EtcSquareMeter },
  { key: 'deposit', term: '보증금', iconName: EtcMeeting },
  { key: 'monthly', term: '임대료', iconName: TagCalendar },
];

type CardRecommendBranchProps = (BranchDetailItem | BranchCompactItem) & {
  theme?: BranchCardTheme;
  className?: string;
};

const BRANCH_BADGE_LABEL: Record<BadgeVariant, string> = {
  recommend: '추천',
  interior: '인테리어 비용 지원 혜택',
};

export default function CardRecommendBranch(props: CardRecommendBranchProps) {
  const { tag, name, href, images, imageAlt, className } = props;
  const [image, imageMo] = images;
  const isDetail = 'details' in props;
  const variantClass = isDetail ? s[`card_theme_${props.theme ?? 'primary'}`] : s.card_compact;

  return (
    <Link
      href={href}
      className={clsx(s.card, variantClass, className)}
      {...gtmClick(GTM_EVENT.BTN, name, href)}
    >
      <div className={s.card_image_area}>
        {tag && tag.length > 0 && (
          <BadgeGroup className={s.card_badge_area}>
            {tag.map((t) => (
              <Badge key={t} variant={t}>
                {BRANCH_BADGE_LABEL[t]}
              </Badge>
            ))}
          </BadgeGroup>
        )}
        <Picture
          src={image}
          mobileSrc={imageMo}
          alt={imageAlt ?? name}
          className={s.card_image}
        />
      </div>
      <div className={s.card_info}>
        <div className={s.card_title_box}>
          {!isDetail && props.subTitle && (
            <span className={s.card_sub_title}>{props.subTitle}</span>
          )}
          <h3 className={s.card_name}>{name}</h3>
          {isDetail && props.location && (
            <span className={s.card_location}>{props.location}</span>
          )}
        </div>
        {isDetail && (
          <dl className={s.info_detail}>
            {DETAIL_FIELDS.map(({ key, term, iconName }) => (
              <div key={key} className={s.info_detail_item}>
                <dt className={s.info_dt}>
                  <Icon name={iconName} size={16} color="var(--color-gray-900)" />
                  <span>{term}</span>
                </dt>
                <dd className={s.info_dd}>{props.details[key]}</dd>
              </div>
            ))}
          </dl>
        )}
        <div className={`prd_btn_more ${s.card_more}`}>
          자세히 알아보기
          <Icon name={ArrowArrow} direction="right" size={16} />
        </div>
      </div>
    </Link>
  );
}
